import React, { useState, useMemo } from 'react';
import QtyInput from './QtyInput';
import { useIsMobile } from '../useIsMobile';
import Hint from './Hint';
import { useLang } from '../i18n/LangContext';
import BiName, { buildZhMap, biText } from '../i18n/BiName';
import { usableG } from '../utils/priceYield';   // V152 실사용량 (신선식품 로스 제외)

function OrderSheet({ categories, prices, onAddToPurchaseOrder, selectedServings, onSelectedServingsChange, terms = {} }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const T = useMemo(() => ({
    ingredient: '식재료', recipeItem: '메뉴', orderTab: '단체주문',
    orderInputLabel: '인분 수', lossRateEnabled: false,
    orderRowLabel: (name, v) => `${name} ${v}인분`,
    calcRatio: (recipe, value) =>
      recipe.serving_g > 0 ? (recipe.serving_g * value) / recipe.total_output : value,
    ...terms,
  }), [terms.lossRateEnabled, terms.ingredient, terms.recipeItem, terms.orderInputLabel]); // eslint-disable-line
  const setSelectedServings = onSelectedServingsChange;
  const [confirmed, setConfirmed] = useState(false);
  const isMobile = useIsMobile();
  const [mobilePanelTab, setMobilePanelTab] = useState('select'); // 'select' | 'result'

  const allRecipes = useMemo(() =>
    categories.flatMap(c => c.items.map(item => ({ ...item, categoryName: c.name }))),
    [categories]
  );

  // V111: 한중 병기 — 재료는 단가표 name_zh, 반제품·메뉴는 레시피 name_zh에서 찾는다
  const { t, lang, fmt: money } = useLang();
  const zhMap = useMemo(() => {
    const m = buildZhMap(prices || []);
    for (const r of allRecipes) {
      const ko = String(r?.name || '').trim();
      const zh = String(r?.name_zh || '').trim();
      if (ko && zh && !m.has(ko)) m.set(ko, zh);
    }
    return m;
  }, [prices, allRecipes]);

  const toggleRecipe = (id) => {
    setSelectedServings(prev => {
      if (prev[id] !== undefined) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: 1 };
    });
  };

  const handleShowResult = () => {
    if (isMobile) setMobilePanelTab('result');
  };

  const selectedCount = Object.keys(selectedServings).length;

  // 재료/자재 집계 — 사용 레시피 정보 포함
  const aggregated = useMemo(() => {
    const map = {};
    Object.entries(selectedServings).forEach(([id, value]) => {
      const recipe = allRecipes.find(r => r.id === id);
      if (!recipe) return;
      const ratio = T.calcRatio(recipe, value);
      const label = T.orderRowLabel(recipe.name, value);
      recipe.ingredients.forEach(ing => {
        // 손실률 적용 (인테리어 모드)
        const lossMultiplier = T.lossRateEnabled ? (1 + (ing.lossRate || 0) / 100) : 1;
        const qty = ing.qty * ratio * lossMultiplier;
        const unit = ing.unit || '';
        const key = ing.name;
        if (!map[key]) map[key] = { name: ing.name, qty: 0, unit, recipes: [] };
        map[key].qty += qty;
        if (!map[key].recipes.includes(label)) map[key].recipes.push(label);
      });
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  }, [selectedServings, allRecipes, T]);

  // 원재료 분해 — 반제품을 원재료로 재귀 분해
  const breakdown = useMemo(() => {
    const map = {};
    const decompose = (ingName, qty, sourceLabel) => {
      const semiRecipe = allRecipes.find(r => r.name === ingName && r.type === 'semi');
      if (semiRecipe && semiRecipe.total_output > 0) {
        const ratio = qty / semiRecipe.total_output;
        semiRecipe.ingredients.forEach(sub => {
          decompose(sub.name, sub.qty * ratio, sourceLabel);
        });
      } else {
        if (!map[ingName]) map[ingName] = { name: ingName, qty: 0, recipes: [] };
        map[ingName].qty += qty;
        if (!map[ingName].recipes.includes(sourceLabel)) map[ingName].recipes.push(sourceLabel);
      }
    };
    aggregated.forEach(row => {
      row.recipes.forEach(label => {
        const semiRecipe = allRecipes.find(r => r.name === row.name && r.type === 'semi');
        if (semiRecipe) {
          decompose(row.name, row.qty, label);
        } else {
          if (!map[row.name]) map[row.name] = { name: row.name, qty: 0, recipes: [] };
          map[row.name].qty += row.qty;
          row.recipes.forEach(r => { if (!map[row.name].recipes.includes(r)) map[row.name].recipes.push(r); });
        }
      });
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  }, [aggregated, allRecipes]);

  const getPriceInfo = (name) => prices.find(p => p.name === name);

  const getOrderQty = (name, qty) => {
    const p = getPriceInfo(name);
    if (!p || !usableG(p)) return null;
    return { qty: qty / usableG(p), unit: p.unit || 'kg' };   // V152 로스만큼 더 산다
  };

  const getCost = (name, qty) => {
    const p = getPriceInfo(name);
    if (!p || !usableG(p)) return 0;
    return Math.round((qty / usableG(p)) * p.price);
  };

  const totalCost1 = aggregated.reduce((s, r) => s + getCost(r.name, r.qty), 0);
  const totalCost2 = breakdown.reduce((s, r) => s + getCost(r.name, r.qty), 0);

  const today = new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });

  // 발주서에 추가
  const handleAddToPurchaseOrder = () => {
    const orderUpdate = {};
    breakdown.forEach(row => {
      const p = getPriceInfo(row.name);
      if (p && usableG(p)) {
        orderUpdate[row.name] = row.qty / usableG(p); // 소수값 그대로 전달 (Math.ceil은 PurchaseOrder에서). V152 실사용량 기준
      }
    });
    onAddToPurchaseOrder(orderUpdate);
    setConfirmed(true);
    setTimeout(() => setConfirmed(false), 2500);
  };

  // 텍스트 복사
  const handleCopy = (rows, title) => {
    const lines = [`[${title}] ${today}`, ''];
    rows.forEach((r, i) => {
      const o = getOrderQty(r.name, r.qty);
      lines.push(`${i + 1}. ${biText(zhMap, r.name)} | 필요량: ${r.qty.toFixed(1)}g | 발주: ${o ? `${o.qty.toFixed(2)} ${o.unit}` : '-'} | ${money(getCost(r.name, r.qty))}`);
    });
    navigator.clipboard.writeText(lines.join('\n'));
  };

  // 주문 구성 레이블
  const orderLabels = Object.entries(selectedServings).map(([id, v]) => {
    const r = allRecipes.find(r => r.id === id);
    return r ? T.orderRowLabel(r.name + ' ×', v) : null;
  }).filter(Boolean);

  // 테이블 컴포넌트
  const Table = ({ rows, title, note, showCopy, showPrint, showAdd }) => (
    <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb',
                  overflow: 'hidden', marginBottom: '28px' }}>
      {/* 테이블 헤더 */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <span style={{ fontSize: '15px', fontWeight: '800', color: '#18181B' }}>📋 {title}</span>
          <span style={{ fontSize: '13px', color: '#9ca3af', marginLeft: '8px' }}>— {today}</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {showCopy && (
            <button onClick={() => handleCopy(rows, title)}
              style={{ padding: '6px 12px', borderRadius: '7px', border: '1.5px solid #e5e7eb',
                        background: '#fff', color: '#374151', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
              📄 {t('텍스트 복사')}
            </button>
          )}
          {showPrint && (
            <button onClick={() => window.print()}
              style={{ padding: '6px 12px', borderRadius: '7px', border: '1.5px solid #e5e7eb',
                        background: '#fff', color: '#374151', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
              🖨️ {t('인쇄')}
            </button>
          )}
          {showAdd && (
            <button onClick={handleAddToPurchaseOrder}
              style={{ padding: '6px 14px', borderRadius: '7px', border: 'none',
                        background: confirmed ? '#6b7280' : '#FF6F0F',
                        color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
              {confirmed ? `✅ ${t('발주서에 추가됨!')}` : `📦 ${t('발주서에 추가')}`}
            </button>
          )}
        </div>
      </div>

      {/* 주문 구성 */}
      {orderLabels.length > 0 && (
        <div style={{ padding: '8px 18px', background: '#FAFAFA', borderBottom: '1px solid #e5e7eb',
                      fontSize: '13px', color: '#374151' }}>
          <span style={{ color: '#9ca3af', marginRight: '6px' }}>{t('주문 구성')}:</span>
          {orderLabels.join(' | ')}
        </div>
      )}

      {/* 안내 메모 */}
      {note && (
        <div style={{ padding: '10px 18px', background: '#FAFAFA', borderBottom: '1px solid #e5e7eb',
                      fontSize: '13px', color: '#374151' }}>
          {note}
        </div>
      )}

      {/* 테이블 */}
      <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '0' : '500px' }}>
        <thead>
          <tr style={{ background: '#1A56A0', color: '#fff' }}>
            {!isMobile && <th style={{ padding: '11px 10px', fontSize: '12px', fontWeight: '700', textAlign: 'center', width: '36px' }}>NO</th>}
            <th style={{ padding: isMobile ? '10px 10px' : '11px 14px', fontSize: '12px', fontWeight: '700', textAlign: 'left' }}>{t(T.ingredient + '명')}</th>
            {showCopy && !isMobile && <th style={{ padding: '11px 14px', fontSize: '12px', fontWeight: '700', textAlign: 'left' }}>{t('사용 ' + T.recipeItem)}</th>}
            {!isMobile && <th style={{ padding: '11px 14px', fontSize: '12px', fontWeight: '700', textAlign: 'right' }}>{t('필요량')}</th>}
            <th style={{ padding: isMobile ? '10px 8px' : '11px 14px', fontSize: '12px', fontWeight: '700', textAlign: 'right', color: '#fff' }}>{t('발주수량')}</th>
            <th style={{ padding: isMobile ? '10px 8px' : '11px 14px', fontSize: '12px', fontWeight: '700', textAlign: 'right', color: '#fff' }}>{t('금액')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const order = getOrderQty(row.name, row.qty);
            const cost = getCost(row.name, row.qty);
            return (
              <tr key={row.name} style={{ borderBottom: '1px solid #f3f4f6',
                                          background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                {!isMobile && <td style={{ padding: '10px 10px', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>{i + 1}</td>}
                <td style={{ padding: isMobile ? '10px 10px' : '10px 14px', fontWeight: '600', fontSize: isMobile ? '13px' : '13px' }}>
                  <BiName ko={row.name} zhMap={zhMap} />
                  {isMobile && showCopy && row.recipes.length > 0 && (
                    <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '400', marginTop: '2px' }}>
                      {row.recipes.map(n => biText(zhMap, n)).join(', ')}
                    </div>
                  )}
                </td>
                {showCopy && !isMobile && (
                  <td style={{ padding: '10px 14px', fontSize: '12px', color: '#6b7280' }}>
                    {row.recipes.map(n => biText(zhMap, n)).join(', ')}
                  </td>
                )}
                {!isMobile && (
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '13px', color: '#374151' }}>
                    {row.qty.toFixed(T.lossRateEnabled ? 2 : 1)}{row.unit ? ` ${row.unit}` : (T.lossRateEnabled ? '' : ' g')}
                  </td>
                )}
                <td style={{ padding: isMobile ? '10px 8px' : '10px 14px', textAlign: 'right', fontSize: '13px',
                              fontWeight: '700', color: '#1e40af' }}>
                  {order ? `${order.qty.toFixed(2)} ${order.unit}` : <span style={{ color: '#d1d5db' }}>-</span>}
                </td>
                <td style={{ padding: isMobile ? '10px 8px' : '10px 14px', textAlign: 'right', fontSize: '13px',
                              fontWeight: '700', color: cost > 0 ? '#FF8F00' : '#d1d5db' }}>
                  {cost > 0 ? money(cost) : '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ background: '#FAFAFA' }}>
            <td colSpan={isMobile ? 2 : (showCopy ? 5 : 4)} style={{ padding: '12px 14px', fontWeight: '800',
                                                      color: '#18181B', fontSize: '14px' }}>
              {t('합계')} ({rows.length})
            </td>
            <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: '800',
                          color: '#18181B', fontSize: '15px' }}>
              {money(showCopy ? totalCost1 : totalCost2)}
            </td>
          </tr>
        </tfoot>
      </table>
      </div> {/* overflowX 래퍼 닫기 */}
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* 모바일 탭 토글 */}
      {isMobile && (
        <div style={{
          display: 'flex', background: '#fff', borderBottom: '1px solid #e5e7eb',
          flexShrink: 0,
        }}>
          {[
            { id: 'select', label: `${t('레시피 선택')}${selectedCount > 0 ? ` (${selectedCount})` : ''}` },
            { id: 'result', label: `${t('발주서')}${selectedCount > 0 ? ` (${selectedCount})` : ''}` },
          ].map(tab => (
            <button key={tab.id} onClick={() => setMobilePanelTab(tab.id)}
              style={{
                flex: 1, padding: '12px 8px', border: 'none', background: 'none',
                fontSize: '14px', fontWeight: '700', cursor: 'pointer',
                color: mobilePanelTab === tab.id ? '#FF6F0F' : '#9ca3af',
                borderBottom: mobilePanelTab === tab.id ? '2.5px solid #FF6F0F' : '2.5px solid transparent',
              }}>
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* 패널 래퍼 */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

      {/* 왼쪽: 레시피 선택 */}
      <div style={{
        width: isMobile ? '100%' : '300px',
        minWidth: isMobile ? 'unset' : '240px',
        borderRight: isMobile ? 'none' : '1px solid #e5e7eb',
        overflowY: 'auto', padding: isMobile ? '16px' : '16px 12px',
        background: '#fff', flexShrink: 0,
        display: isMobile && mobilePanelTab !== 'select' ? 'none' : 'flex',
        flexDirection: 'column',
      }}>
        <Hint hintKey="order_howto" mb={12}
          zh={T.lossRateEnabled ? undefined : <>选择今天要做的<strong>菜品并输入份数</strong>，所需原材料会自动汇总。半成品（酱汁·高汤等）也会自动拆成原材料，不用一个个算！</>}>
          {T.lossRateEnabled
            ? <><strong>시공 항목을 선택하고 면적(m²)을 입력</strong>하면 필요 자재가 자동으로 합산돼요.
               손실률도 자동 반영됩니다!</>
            : <>오늘 만들 <strong>메뉴를 선택하고 인분 수를 입력</strong>하면 필요한 원재료가 자동으로 합산돼요.
               반제품(소스·육수 등)도 원재료로 자동 분해되니 일일이 계산할 필요 없어요!</>
          }
        </Hint>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#18181B' }}>{t(T.recipeItem + ' 선택')}</h3>
            <span style={{ fontSize: '12px', color: '#9ca3af' }}>{T.lossRateEnabled ? `${T.orderInputLabel}을 입력하세요` : t('인분 수를 입력하세요')}</span>
          </div>
          {selectedCount > 0 && (
            <button onClick={() => setSelectedServings({})}
              style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #E4E4E7',
                       background: '#fff5f5', color: '#52525B', fontSize: '12px', cursor: 'pointer' }}>
              {t('초기화')}
            </button>
          )}
        </div>

        {categories.map(cat => (
          <div key={cat.name} style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#9ca3af',
                          padding: '3px 8px', background: '#f9fafb',
                          borderRadius: '5px', marginBottom: '5px' }}>
              {cat.name}
              {cat.name_zh && <span style={{ marginLeft: 6, color: '#1A56A0', fontWeight: 600 }}>{cat.name_zh}</span>}
            </div>
            {cat.items.map(item => {
              const isSelected = selectedServings[item.id] !== undefined;
              return (
                <div key={item.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: isMobile ? '12px 12px' : '7px 10px',
                    marginBottom: '4px', borderRadius: '8px',
                    border: `1.5px solid ${isSelected ? '#FF6F0F' : '#f0f0f0'}`,
                    background: isSelected ? '#FAFAFA' : '#fff', cursor: 'pointer',
                    minHeight: isMobile ? '52px' : 'auto',
                  }}
                  onClick={() => toggleRecipe(item.id)}
                >
                  <div style={{
                    width: isMobile ? '20px' : '16px',
                    height: isMobile ? '20px' : '16px',
                    borderRadius: '4px', flexShrink: 0,
                    border: `2px solid ${isSelected ? '#FF6F0F' : '#d1d5db'}`,
                    background: isSelected ? '#FF6F0F' : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: isMobile ? '12px' : '10px', fontWeight: '700',
                  }}>
                    {isSelected ? '✓' : ''}
                  </div>
                  <span style={{ flex: 1, fontSize: isMobile ? '15px' : '13px',
                                  fontWeight: isSelected ? '700' : '400',
                                  color: isSelected ? '#FF6F0F' : '#374151' }}>
                    <BiName ko={item.name} zh={item.name_zh} size={isMobile ? 12 : 11} />
                  </span>
                  {isSelected && (
                    <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <QtyInput
                        value={selectedServings[item.id]}
                        onChange={v => setSelectedServings(prev => ({ ...prev, [item.id]: Math.max(1, v) }))}
                        min={1} size="sm"
                      />
                      {T.lossRateEnabled && (
                        <span style={{ fontSize: '11px', color: '#6b7280', flexShrink: 0 }}>m²</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {/* 모바일: 발주서 보기 버튼 */}
        {isMobile && selectedCount > 0 && (
          <button onClick={handleShowResult}
            style={{
              width: '100%', padding: '14px', marginTop: '8px',
              borderRadius: '10px', border: 'none',
              background: '#FF6F0F', color: '#fff',
              fontSize: '15px', fontWeight: '800', cursor: 'pointer',
            }}>
            {t('발주서 보기')} ({selectedCount}) →
          </button>
        )}
      </div>

      {/* 오른쪽: 발주서 2개 */}
      <div style={{
        flex: 1, padding: isMobile ? '14px' : '20px 24px',
        overflowY: 'auto', background: '#f8fafb',
        display: isMobile && mobilePanelTab !== 'result' ? 'none' : 'block',
      }}>
        {/* 모바일 뒤로가기 */}
        {isMobile && (
          <button onClick={() => setMobilePanelTab('select')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: 'none', border: 'none', color: '#18181B',
              fontSize: '14px', fontWeight: '700', cursor: 'pointer',
              marginBottom: '12px', padding: '4px 0',
            }}>
            ‹ {t('레시피 선택으로')}
          </button>
        )}

        {selectedCount === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', height: '60%', color: '#9ca3af' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>
              {T.lossRateEnabled ? '📐' : '👥'}
            </div>
            <p style={{ fontSize: '15px' }}>
              {T.lossRateEnabled
                ? (isMobile
                    ? `왼쪽 탭에서 ${T.recipeItem}을 선택하세요`
                    : `왼쪽에서 ${T.recipeItem}과 ${T.orderInputLabel}을 선택하세요`)
                : t(isMobile ? '왼쪽 탭에서 메뉴를 선택하세요' : '왼쪽에서 메뉴와 인분 수를 선택하세요')}
            </p>
            <p style={{ fontSize: '13px', marginTop: '6px', color: '#d1d5db' }}>
              {t('선택하면 자재 발주서가 자동 생성돼요')}
            </p>
          </div>
        ) : (
          <>
            {/* 발주서 */}
            <Table
              rows={aggregated}
              title={t('발주서')}
              showCopy={true}
              showPrint={true}
              showAdd={false}
            />

            {/* 원재료 분해 발주서 */}
            <Table
              rows={breakdown}
              title={t('원재료 분해 발주서')}
              note={t('반제품을 원재료로 분해한 발주서입니다. 위 발주서와 비교하여 사용하세요.')}
              showCopy={false}
              showPrint={false}
              showAdd={true}
            />
          </>
        )}
      </div>
      </div> {/* 패널 래퍼 닫기 */}
    </div>
  );
}

export default OrderSheet;
